import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  X,
  Check,
  Loader2,
  Save,
  Bookmark,
  Trash2
} from 'lucide-react';
import UniversalTable from '../services/universalTable'; // Import the UniversalTable component

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Multi-Select Filter Component
const MultiSelectFilter = ({ label, options, selectedValues, onChange, renderOption = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option => {
    const searchText = typeof option === 'string' ? option : (option.name || option.value || '');
    return searchText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const toggleOption = (option) => {
    const value = typeof option === 'string' ? option : (option.value || option.name);
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return `Select ${label}...`;
    if (selectedValues.length === 1) return selectedValues[0];
    return `${selectedValues.length} selected`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          isOpen ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
        }`}
      >
        <span className={`text-sm truncate ${
          selectedValues.length === 0 ? 'text-gray-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100'
        }`}>
          {getDisplayText()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl animate-in slide-in-from-top-2 duration-200">
          <div className="p-3 border-b border-gray-200 dark:border-slate-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">No options found</div>
            ) : (
              filteredOptions.map((option, index) => {
                const value = typeof option === 'string' ? option : (option.value || option.name);
                const isSelected = selectedValues.includes(value);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-150 text-left ${
                      isSelected ? 'bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {renderOption ? renderOption(option) : (
                        <span className="truncate">
                          {typeof option === 'string' ? option : (option.name || option.value)}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedValues.map((value, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs animate-in slide-in-from-left-2 duration-200"
            >
              <span className="truncate max-w-24">{value}</span>
              <button
                type="button"
                onClick={() => onChange(selectedValues.filter(v => v !== value))}
                className="hover:bg-blue-200 dark:hover:bg-blue-800/50 rounded-full p-0.5 transition-colors flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Single-Select Filter Component
const SingleSelectFilter = ({ label, options, selectedValue, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getDisplayText = () => {
    if (!selectedValue) return `Select ${label}...`;
    return selectedValue;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          isOpen ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
        }`}
      >
        <span className={`text-sm truncate ${
          !selectedValue ? 'text-gray-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100'
        }`}>
          {getDisplayText()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl animate-in slide-in-from-top-2 duration-200">
          <div className="max-h-60 overflow-y-auto">
            {options.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 text-sm hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-150 text-left ${
                  selectedValue === option ? 'bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-slate-100'
                }`}
              >
                <span className="truncate">{option}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const AffiliateRecruitment = () => {
  // State variables
  const [filteredData, setFilteredData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [alreadyApproached, setAlreadyApproached] = useState('');
  const [countryData, setCountryData] = useState([]);
  const [existingAffiliateData, setExistingAffiliateData] = useState([]);
  const [isLoadingExistingAffiliates, setIsLoadingExistingAffiliates] = useState(false);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [userPlan, setUserPlan] = useState('Free'); // Add user plan state

  // Filter states
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [selectedTrafficSources, setSelectedTrafficSources] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [sortByLatest, setSortByLatest] = useState(false); // Toggle: false = organic traffic DESC, true = created_at DESC

  // Store current filters for pagination
  const [currentFilters, setCurrentFilters] = useState({});
  // ADDED: Store current page size
  const [currentPageSize, setCurrentPageSize] = useState(10);
  // ADDED: Search query state for server-side search
  const [searchQuery, setSearchQuery] = useState('');

  // Saved filters state
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  const [saveFilterError, setSaveFilterError] = useState('');

  // Function to get plan limits for non-blurred images
  const getPlanImageLimit = (plan) => {
    const planLimits = {
      'Free': 0,
      'Growth': 25,
      'Pro': 500,
      'Enterprise': Infinity
    };
    return planLimits[plan] || 0;
  };

  // Fetch user info to get the plan
  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${basePath}/api/userprofile`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.success && userData.data?.plan) {
          const raw = String(userData.data.plan);
          const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
          setUserPlan(normalized);
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Default to Free plan if we can't fetch user info
      setUserPlan('Free');
    }
  };

  // Fetch existing affiliate data
  const fetchExistingAffiliate = async () => {
    setIsLoadingExistingAffiliates(true);
    setApiError(null);
    
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/existing`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      if (responseData.success) {
        setExistingAffiliateData(responseData.data.affiliates || []);
        setCountryData(responseData.data.countries || []);
      } else {
        throw new Error(responseData.message || 'Failed to fetch data');
      }
      
    } catch (error) {
      console.error("Error fetching existing affiliates:", error);
      setApiError(error.message);
      
      // Fallback data
      setExistingAffiliateData([
        { name: 'Monster Casino', value: 'monstercasino.com' },
        { name: 'Swift Casino', value: 'swiftcasino.com' },
        { name: 'Royal Bingo', value: 'royalbingo.com' },
        { name: 'Lucky Sports', value: 'luckysports.com' },
        { name: 'Vegas Poker', value: 'vegaspoker.com' }
      ]);
      
      setCountryData([
        { code: 'GB', name: 'United Kingdom' },
        { code: 'US', name: 'United States' },
        { code: 'DE', name: 'Germany' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' }
      ]);
    } finally {
      setIsLoadingExistingAffiliates(false);
    }
  };

  const retryFetchExistingAffiliates = () => {
    fetchExistingAffiliate();
  };

  // Fetch saved filters
  const fetchSavedFilters = async () => {
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/saved-filters`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setSavedFilters(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved filters:', error);
    }
  };

  // Save current filters
  const isFilterEmpty = () => {
    return selectedVerticals.length === 0 &&
      selectedTrafficSources.length === 0 &&
      !selectedBrands &&
      selectedCountries.length === 0 &&
      !alreadyApproached;
  };

  const handleSaveFilter = async () => {
    if (!saveFilterName.trim()) return;
    if (isFilterEmpty()) {
      setSaveFilterError('Please select at least one filter before saving.');
      return;
    }
    setSaveFilterError('');
    setSavingFilter(true);
    try {
      const filters = {
        verticals: selectedVerticals,
        trafficSources: selectedTrafficSources,
        brands: selectedBrands,
        countries: selectedCountries,
        alreadyApproached: alreadyApproached,
        sortByLatest: sortByLatest
      };
      const response = await fetch(`${basePath}/api/affiliate-recruitment/saved-filters`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: saveFilterName.trim(), filters })
      });
      const data = await response.json();
      if (data.success) {
        setSavedFilters(prev => [...prev, data.data]);
        setShowSaveDialog(false);
        setSaveFilterName('');
        setSaveFilterError('');
      } else {
        setSaveFilterError(data.message || 'Failed to save filter');
      }
    } catch (error) {
      console.error('Error saving filter:', error);
      setSaveFilterError('Failed to save filter');
    } finally {
      setSavingFilter(false);
    }
  };

  // Apply a saved filter
  const applySavedFilter = (savedFilter) => {
    const f = savedFilter.filters;
    setSelectedVerticals(f.verticals || []);
    setSelectedTrafficSources(f.trafficSources || []);
    setSelectedBrands(f.brands || '');
    setSelectedCountries(f.countries || []);
    setAlreadyApproached(f.alreadyApproached || '');
    setSortByLatest(f.sortByLatest || false);

    const filters = {
      verticals: f.verticals || [],
      trafficSources: f.trafficSources || [],
      brands: f.brands || '',
      countries: f.countries || [],
      alreadyApproached: f.alreadyApproached || '',
      sortByLatest: f.sortByLatest || false
    };
    setCurrentFilters(filters);
    fetchDataWithPagination(0, currentPageSize, filters);
  };

  // Delete a saved filter
  const handleDeleteFilter = async (e, filterId) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/saved-filters/${filterId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.success) {
        setSavedFilters(prev => prev.filter(f => f.id !== filterId));
      }
    } catch (error) {
      console.error('Error deleting filter:', error);
    }
  };

  useEffect(() => {
    fetchUserInfo(); // Fetch user plan on component mount
    fetchExistingAffiliate();
    fetchSavedFilters();
  }, []);

  // Filter options - matches DB gambling_types: casino, sports_betting, poker, lottery, bingo, sweepstake, crypto
  const filterOptions = {
    verticals: ['Casino', 'Sports Betting', 'Poker', 'Lottery', 'Bingo', 'Sweepstake', 'Crypto'],
    trafficSource: ['Organic Traffic', 'Paid Traffic'],
    existingAffiliate: existingAffiliateData,
    countries: countryData.map(country => ({
      name: country.name,
      value: country.name,
      flag: `https://flagsapi.com/${country.code}/flat/24.png`
    })),
    alreadyApproachedOptions: ['Yes', 'No']
  };

  // UPDATED: Transform function with proper image URL logic based on plan
  const TransformDataAPI = (data) => {
    const imageLimit = getPlanImageLimit(userPlan);

    const transformedData = data.map((item, index) => {
      // Determine if this row should show the actual image or blur placeholder
      let imageUrl = 'blurrow.png'; // Default to blur image

      if (userPlan === 'Enterprise') {
        // Enterprise gets real images for all
        imageUrl = item.screenshot_url || item.imageUrl || 'blurrow.png';
      } else if (userPlan === 'Pro' && index < 500) {
        // Pro gets real images for first 500
        imageUrl = item.screenshot_url || item.imageUrl || 'blurrow.png';
      } else if (userPlan === 'Growth' && index < 25) {
        // Growth gets real images for first 25
        imageUrl = item.screenshot_url || item.imageUrl || 'blurrow.png';
      }
      // Free plan always gets 'blurrow.png' (already set as default)

      return {
        paid_traffic: item.adwords_traffic,
        imageUrl: imageUrl, // Use the determined imageUrl
        paid_traffic_difference: item.adwords_traffic_difference,
        affiliate_url: item.affiliate_url,
        already_approached: item.already_approached,
        email: item.email,
        id: item.id,
        keywords: item.keywords,
        organic_traffic: item.organic_traffic,
        organic_traffic_difference: item.organic_traffic_difference,
        paid_keyword: item.paid_keyword,
        adwords_keywords: item.adwords_keywords,
        paid_top_page: item.paid_top_page,
        rank: item.rank,
        rank_difference: item.rank_difference,
        top_geo_organic_traffic: item.top_geo_organic_traffic,
        top_geo_organic_traffic_difference: item.top_geo_organic_traffic_difference,
        top_page: item.top_page,
        total_traffic: item.total_traffic,
        total_traffic_difference: item.total_traffic_difference,
        screenshot_url: item.screenshot_url, // Keep original for reference
        vertical: item.vertical, // Include vertical data
        affiliate_url_confidence: item.affiliate_url_confidence // Classification confidence URL
      };
    });

    return transformedData;
  };

  // UPDATED: Unified fetch data function that handles both search and pagination
  const fetchDataWithPagination = async (page = 0, pageSize = currentPageSize, filters = null) => {
    
    setIsLoadingTableData(true);
    setApiError(null);
    
    const filtersToUse = filters !== null ? filters : currentFilters;
    
    // Calculate offset
    const offset = page * pageSize;
    
    const requestBody = {
      ...filtersToUse,
      page: page,
      pageSize: pageSize,
      offset: offset,
      sortByLatest: filtersToUse.sortByLatest === true, // Explicitly set sortByLatest
      search: filtersToUse.search || searchQuery // Include search query
    };



    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/fetchData`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      if (responseData.success) {
        // UPDATED: Extract user plan from API response if available
        if (responseData.data && responseData.data.user && responseData.data.user.plan) {
          setUserPlan(responseData.data.user.plan);
        }
        
        if (responseData.data && Array.isArray(responseData.data.data)) {
          const transformedData = TransformDataAPI(responseData.data.data);
          setFilteredData(transformedData);
          
          const newPaginationInfo = {
            page: responseData.data.page,
            pageSize: responseData.data.pageSize,
            total: responseData.data.total,
            totalPages: responseData.data.totalPages,
            hasNextPage: responseData.data.hasNextPage,
            hasPreviousPage: responseData.data.hasPreviousPage,
            offset: offset
          };
          
          setPaginationInfo(newPaginationInfo);
          
        } else if (Array.isArray(responseData.data)) {
          const transformedData = TransformDataAPI(responseData.data);
          setFilteredData(transformedData);
        } else {
          console.error('Unexpected data structure:', responseData.data);
          setFilteredData([]);
          setApiError('Unexpected data structure received from API');
        }
      } else {
        throw new Error(responseData.message || 'No data received');
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setApiError(error.message);
      setFilteredData([]);
    } finally {
      setIsLoadingTableData(false);
    }
  };

  // UPDATED: Page change handler with current page size
  const handlePageChange = async (newPage) => {
    
    try {
      // Force re-render by updating pagination info immediately
      setPaginationInfo(prev => prev ? { ...prev, page: newPage } : null);
      
      await fetchDataWithPagination(newPage, currentPageSize, currentFilters);
    } catch (error) {
      console.error('Error during page change:', error);
      setApiError('Failed to load page data');
      // Revert pagination info on error
      setPaginationInfo(prev => prev ? { ...prev, page: prev.page } : null);
    }
  };

  // ADDED: Page size change handler
  const handlePageSizeChange = async (newPageSize) => {
    
    // Update the current page size state
    setCurrentPageSize(newPageSize);
    
    try {
      // Reset to first page when changing page size
      const newPage = 0;
      
      // Update pagination info immediately to reflect the change
      setPaginationInfo(prev => prev ? { 
        ...prev, 
        page: newPage, 
        pageSize: newPageSize,
        totalPages: Math.ceil(prev.total / newPageSize)
      } : null);
      
      await fetchDataWithPagination(newPage, newPageSize, currentFilters);
    } catch (error) {
      console.error('Error during page size change:', error);
      setApiError('Failed to load data with new page size');
      // Revert page size on error
      setCurrentPageSize(paginationInfo?.pageSize || 10);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const filters = {
      verticals: selectedVerticals,
      trafficSources: selectedTrafficSources,
      brands: selectedBrands,
      countries: selectedCountries,
      alreadyApproached: alreadyApproached,
      sortByLatest: sortByLatest // Include sorting preference in filters
    };

    // Store current filters for pagination
    setCurrentFilters(filters);

    // Start from first page with current page size
    await fetchDataWithPagination(0, currentPageSize, filters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedVerticals([]);
    setSelectedTrafficSources([]);
    setSelectedBrands('');
    setSelectedCountries([]);
    setAlreadyApproached('');
    setSortByLatest(false); // Reset sorting toggle
    setFilteredData([]);
    setPaginationInfo(null);
    setApiError(null);
    setCurrentFilters({});
    // Reset page size to default
    setCurrentPageSize(10);
  };

  // Handle deleting a prospect
  const handleDeleteProspect = async (prospect) => {
    if (!prospect?.id) return;
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/prospect/${prospect.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.success) {
        // Remove from local state immediately
        setFilteredData(prev => prev.filter(item => item.id !== prospect.id));
        // Update pagination total
        setPaginationInfo(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev);
      } else {
        alert(data.message || 'Failed to delete prospect');
      }
    } catch (error) {
      console.error('Error deleting prospect:', error);
      alert('Failed to delete prospect');
    }
  };

  // Handle sortByLatest toggle change from UniversalTable
  const handleSortByLatestChange = (newValue) => {
    setSortByLatest(newValue);
    // Auto-fetch with current filters when toggle changes
    const filters = {
      verticals: selectedVerticals,
      trafficSources: selectedTrafficSources,
      brands: selectedBrands,
      countries: selectedCountries,
      alreadyApproached: alreadyApproached,
      sortByLatest: newValue,
      search: searchQuery // Include search in filters
    };
    setCurrentFilters(filters);
    fetchDataWithPagination(0, currentPageSize, filters);
  };

  // ADDED: Handle server-side search - wrapped in useCallback for stable reference
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);

    // Update filters with search query
    const filters = {
      ...currentFilters,
      search: query
    };
    setCurrentFilters(filters);

    // Reset to first page and fetch with search
    fetchDataWithPagination(0, currentPageSize, filters);
  }, [currentFilters, currentPageSize]);

  // Debug: Log handleSearch function reference
  useEffect(() => {
  }, [handleSearch]);

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">

      {/* Header section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
                Affiliate Recruitment
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Use filters to find and retrieve affiliate data
              </p>
              {/* ADDED: Display current user plan with image preview info */}
              
            </div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {apiError && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400 dark:text-red-300" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {apiError}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Search Filters</h2>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {savedFilters.map((sf) => (
                <button
                  key={sf.id}
                  onClick={() => applySavedFilter(sf)}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-700 dark:text-slate-300 hover:text-blue-700 dark:hover:text-blue-300 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-lg text-sm transition-all duration-200"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  <span>{sf.name}</span>
                  <span
                    onClick={(e) => handleDeleteFilter(e, sf.id)}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 dark:hover:text-red-400 transition-all duration-200 p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </span>
                </button>
              ))}
              {savedFilters.length < 6 && (
                <button
                  onClick={() => { setShowSaveDialog(true); setSaveFilterError(''); }}
                  className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-1.5"
                >
                  <Save className="w-4 h-4" />
                  Save Filter
                </button>
              )}
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Save filter dialog */}
          {showSaveDialog && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Filter name (e.g. UK Casinos)"
                  value={saveFilterName}
                  onChange={(e) => setSaveFilterName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveFilter()}
                  maxLength={50}
                  className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-600 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  autoFocus
                />
                <button
                  onClick={handleSaveFilter}
                  disabled={!saveFilterName.trim() || savingFilter}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5"
                >
                  {savingFilter ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                <button
                  onClick={() => { setShowSaveDialog(false); setSaveFilterName(''); setSaveFilterError(''); }}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 text-sm"
                >
                  Cancel
                </button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {savedFilters.length}/6 filters saved
                </p>
                {saveFilterError && (
                  <p className="text-xs text-red-500 dark:text-red-400">{saveFilterError}</p>
                )}
              </div>
            </div>
          )}

          {/* Saved filter buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 items-start">

            <MultiSelectFilter
              label="Verticals"
              options={filterOptions.verticals}
              selectedValues={selectedVerticals}
              onChange={setSelectedVerticals}
            />

            <MultiSelectFilter
              label="Traffic Source"
              options={filterOptions.trafficSource}
              selectedValues={selectedTrafficSources}
              onChange={setSelectedTrafficSources}
            />

            <div className="relative">
              <SingleSelectFilter
                label="Existing Affiliate Brands"
                options={filterOptions.existingAffiliate.map(brand => brand.name || brand)}
                selectedValue={filterOptions.existingAffiliate.find(b => b.value === selectedBrands)?.name || selectedBrands}
                onChange={(brandName) => {
                  // Find the brand object and store the URL value for filtering
                  const brand = filterOptions.existingAffiliate.find(b => b.name === brandName);
                  setSelectedBrands(brand ? brand.value : brandName);
                }}
              />
              
              {isLoadingExistingAffiliates && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-sm p-3 z-50">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading existing affiliates...
                  </div>
                </div>
              )}

              {apiError && !isLoadingExistingAffiliates && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-lg shadow-sm p-3 z-50">
                  <div className="text-sm text-red-600 dark:text-red-400 mb-2">Failed to load affiliates</div>
                  <button
                    onClick={retryFetchExistingAffiliates}
                    className="text-xs bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 px-2 py-1 rounded border border-red-200 dark:border-red-800 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>

            <MultiSelectFilter
              label="Countries"
              options={filterOptions.countries}
              selectedValues={selectedCountries}
              onChange={setSelectedCountries}
              renderOption={(country) => (
                <div className="flex items-center gap-2 min-w-0">
                  <img 
                    src={country.flag} 
                    alt={country.name}
                    className="w-6 h-4 flex-shrink-0"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ddd"/></svg>';
                    }}
                  />
                  <span className="text-sm truncate">{country.name || country}</span>
                </div>
              )}
            />

            <SingleSelectFilter
              label="Already Approached"
              options={filterOptions.alreadyApproachedOptions}
              selectedValue={alreadyApproached}
              onChange={setAlreadyApproached}
            />

            <div>
              <label className="block text-sm font-medium text-transparent mb-2">&nbsp;</label>
              <button
                onClick={handleSubmit}
                disabled={isLoadingTableData}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoadingTableData ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Search Affiliates</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* UPDATED: Results section - Now passing userPlan to UniversalTable */}
      {filteredData.length > 0 && (
        (() => {
          return (
            <UniversalTable
              data={filteredData}
              paginationInfo={paginationInfo}
              onFetchData={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSearch={handleSearch}
              title="Search Results"
              searchable={true}
              defaultPageSize={currentPageSize}
              loading={isLoadingTableData}
              userPlan={userPlan}
              sortByLatest={sortByLatest}
              onSortByLatestChange={handleSortByLatestChange}
              onDelete={handleDeleteProspect}
            />
          );
        })()
      )}
    </div>
  );
};

export default AffiliateRecruitment;